import { useState, useEffect, useCallback, useMemo } from 'react';
import { HeartPulse, RefreshCw, CheckCircle2, AlertTriangle, XCircle, List, X, Download } from 'lucide-react';
import api from '@/utils/api';
import { toastMessage } from '@/utils/toast-message';
import { downloadCSV } from '@/utils/csv';

interface Point { day: string; value: number; }
interface HealthCheck {
  key: string;
  group: string;
  label: string;
  why: string;
  growth_only: boolean;
  value: number | null;
  yesterday: number | null;
  breakdown: { label: string; n: number }[];
  computed_at: string | null;
  status: 'ok' | 'attention' | 'error' | 'not_run';
  trend: Point[];
}
interface HealthPage { success: boolean; checks: HealthCheck[]; }
interface OwnershipSource { source: string; today: number; moves: number; from_another_agent: number; worked_moved: number; overrides: number; }
interface OwnershipReport { days: number; since: string; sources: OwnershipSource[]; releases: { reason: string; n: number }[]; }
interface SpeedRow { key: string; opened: number; contacted: number; contacted_pct: number | null; within_24h_pct: number | null; median_hours: number | null; }
interface SpeedReport { by_type: SpeedRow[]; by_owner: SpeedRow[]; computed_at?: string; }
const TYPE_LABEL: Record<string, string> = { edd: 'EDD', ndr: 'NDR', rto: 'RTO', cod_confirm: 'Confirm Orders' };
const hrs = (h: number | null) => (h === null ? '—' : h < 1 ? `${Math.round(h * 60)} min` : h < 48 ? `${h} h` : `${Math.round(h / 24)} d`);

const num = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString('en-IN'));
const fmtTime = (s: string | null) =>
  s ? new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const STATUS = {
  ok:        { icon: <CheckCircle2 className="h-4 w-4" />, label: 'Clean', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  attention: { icon: <AlertTriangle className="h-4 w-4" />, label: 'Needs attention', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  error:     { icon: <XCircle className="h-4 w-4" />, label: 'Check failed', cls: 'bg-red-50 text-red-700 border-red-200' },
  not_run:   { icon: <RefreshCw className="h-4 w-4" />, label: 'Not run yet', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
};

/** Tiny bar chart of the last days, newest on the right. */
const Spark: React.FC<{ points: Point[] }> = ({ points }) => {
  if (points.length < 2) return null;
  const max = Math.max(...points.map(p => p.value), 1);
  return (
    <div className="flex items-end gap-0.5 h-6" title={points.map(p => `${p.day}: ${p.value}`).join('\n')}>
      {points.map(p => (
        <div key={p.day} className="w-2 bg-gray-300 dark:bg-gray-600 rounded-sm"
             style={{ height: `${Math.max(8, (p.value / max) * 100)}%` }} />
      ))}
    </div>
  );
};

/**
 * Daily Health -- one number per operating rule (no order missed, no paisa missed,
 * never wipe an agent's work, no duplicates). Backend: /reports/daily-health,
 * app/services/daily_health_service.py. Runs every morning at 08:25 IST and emails
 * tech@zeroharm.in whatever is not clean.
 */
const DailyHealth = () => {
  const [page, setPage] = useState<HealthPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [speed, setSpeed] = useState<SpeedReport | null>(null);
  const [speedLoading, setSpeedLoading] = useState(false);
  const loadSpeed = useCallback(async () => {
    setSpeedLoading(true);
    try {
      setSpeed(await api.req({ url: '/reports/work-items/response-speed?days=7', method: api.Method.GET }) as unknown as SpeedReport);
    } catch {
      toastMessage('Could not load response speed', 'error');
    } finally {
      setSpeedLoading(false);
    }
  }, []);
  useEffect(() => { loadSpeed(); }, [loadSpeed]);
  // Customer-owner changes by source, live: a new ownership leak shows up the day it starts.
  const [ownership, setOwnership] = useState<OwnershipReport | null>(null);
  const loadOwnership = useCallback(async () => {
    try {
      const res = await api.req({ url: '/reports/daily-health/ownership-changes', method: api.Method.GET }) as unknown as
        { data?: OwnershipReport };
      setOwnership(res?.data ?? null);
    } catch {
      toastMessage('Could not load ownership changes', 'error');
    }
  }, []);
  useEffect(() => { loadOwnership(); }, [loadOwnership]);
  const [list, setList] = useState<{ check: HealthCheck; rows: Record<string, unknown>[]; loading: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPage(await api.req({ url: '/reports/daily-health', method: api.Method.GET }) as unknown as HealthPage);
    } catch {
      toastMessage('Could not load Daily Health', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      setPage(await api.req({ url: '/reports/daily-health/run', method: api.Method.POST, data: {} }) as unknown as HealthPage);
      toastMessage('Checks re-run', 'success');
    } catch {
      toastMessage('Could not run the checks', 'error');
    } finally {
      setRunning(false);
    }
  };

  const openList = async (check: HealthCheck) => {
    setList({ check, rows: [], loading: true });
    try {
      const res = await api.req({ url: `/reports/daily-health/${check.key}/items`, method: api.Method.GET }) as unknown as
        { data?: Record<string, unknown>[] };
      setList({ check, rows: res?.data || [], loading: false });
    } catch {
      toastMessage('Could not load the list', 'error');
      setList(null);
    }
  };

  const groups = useMemo(() => {
    const g: Record<string, HealthCheck[]> = {};
    (page?.checks || []).forEach(c => { (g[c.group] = g[c.group] || []).push(c); });
    return g;
  }, [page]);
  const needAttention = (page?.checks || []).filter(c => c.status === 'attention' || c.status === 'error').length;
  const lastRun = (page?.checks || []).map(c => c.computed_at).filter(Boolean).sort().pop() || null;
  const listCols = list?.rows.length ? Object.keys(list.rows[0]) : [];

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-brandColor p-2 rounded-lg"><HeartPulse className="h-5 w-5 text-white" /></div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Daily Health</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Did anything slip through? One number per rule — should be 0, or at least not growing.
              {lastRun && <span className="ml-2 text-gray-400">· Last run {fmtTime(lastRun)}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {page && (
            <span className={`text-xs px-2.5 py-1 rounded-full border ${needAttention ? STATUS.attention.cls : STATUS.ok.cls}`}>
              {needAttention ? `${needAttention} need attention` : 'All clear'}
            </span>
          )}
          <button onClick={runNow} disabled={running || loading}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-brandColor text-white rounded-lg text-sm font-medium disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${running ? 'animate-spin' : ''}`} />{running ? 'Running… (~20s)' : 'Run checks now'}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-6">
        {!page && <p className="text-sm text-gray-400">{loading ? 'Loading…' : ''}</p>}
        {Object.entries(groups).map(([group, checks]) => (
          <div key={group}>
            <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{group}</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {checks.map(c => {
                const st = STATUS[c.status];
                const delta = c.value !== null && c.yesterday !== null ? c.value - c.yesterday : null;
                return (
                  <div key={c.key} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white">{c.label}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{c.why}</p>
                      </div>
                      <span className={`shrink-0 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${st.cls}`}>
                        {st.icon}{st.label}
                      </span>
                    </div>
                    <div className="flex items-end justify-between mt-3">
                      <div>
                        <span className="text-3xl font-bold text-gray-900 dark:text-white">{num(c.value)}</span>
                        <span className="ml-2 text-xs text-gray-400">
                          {c.yesterday !== null ? `yesterday ${num(c.yesterday)}` : ''}
                          {delta !== null && delta !== 0 && (
                            <span className={delta > 0 ? 'text-red-500 ml-1' : 'text-emerald-600 ml-1'}>
                              ({delta > 0 ? '+' : ''}{num(delta)})
                            </span>
                          )}
                        </span>
                      </div>
                      <Spark points={c.trend} />
                    </div>
                    {c.breakdown.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {c.breakdown.slice(0, 8).map(b => (
                          <span key={b.label} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            {b.label}: <b>{num(b.n)}</b>
                          </span>
                        ))}
                      </div>
                    )}
                    {(c.value ?? 0) > 0 && (
                      <button onClick={() => openList(c)}
                              className="mt-3 inline-flex items-center gap-1 text-xs text-brandColor hover:underline">
                        <List className="h-3.5 w-3.5" />View the list
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Response speed: time from a work item opening to the first contact about it */}
        <div>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Response speed · last 7 days</h2>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 mb-3">
              First contact = the first outcome or note on the work item, or a call to the customer by its owner after it opened.
              {speed?.computed_at && <span className="ml-1 text-gray-400">· as of {fmtTime(speed.computed_at)}</span>}
            </p>
            {speedLoading && !speed && <p className="text-sm text-gray-400">Loading… (~10s)</p>}
            {speed && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {([['By work type', speed.by_type, true], ['By owner', speed.by_owner, false]] as [string, SpeedRow[], boolean][]).map(([title, rows, isType]) => (
                  <div key={title}>
                    <p className="text-xs font-semibold text-gray-600 mb-1">{title}</p>
                    <table className="w-full text-sm">
                      <thead><tr className="text-xs text-gray-400 text-left">
                        <th className="py-1 pr-2 font-medium">{isType ? 'Type' : 'Owner'}</th>
                        <th className="py-1 pr-2 font-medium text-right">Opened</th>
                        <th className="py-1 pr-2 font-medium text-right">Contacted</th>
                        <th className="py-1 pr-2 font-medium text-right">Within 24h</th>
                        <th className="py-1 font-medium text-right">Median to first contact</th>
                      </tr></thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {rows.slice(0, isType ? 10 : 15).map(r => (
                          <tr key={r.key}>
                            <td className="py-1 pr-2">{isType ? (TYPE_LABEL[r.key] || r.key) : r.key}</td>
                            <td className="py-1 pr-2 text-right">{num(r.opened)}</td>
                            <td className="py-1 pr-2 text-right">{r.contacted_pct === null ? '—' : `${r.contacted_pct}%`}</td>
                            <td className={`py-1 pr-2 text-right ${r.within_24h_pct !== null && r.within_24h_pct < 50 ? 'text-red-600' : ''}`}>{r.within_24h_pct === null ? '—' : `${r.within_24h_pct}%`}</td>
                            <td className="py-1 text-right">{hrs(r.median_hours)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Ownership changes by source: one owner A-to-Z, so "worked, moved" should read 0 */}
        <div>
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Ownership changes by source · last {ownership?.days ?? 7} days
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-xs text-gray-500 mb-3">
              Every change of a customer's owner, by where it came from. A customer an agent has worked stays with
              them until a super admin overrides it (recorded) or the agent is deactivated, so <b>Worked, moved</b> should be 0.
            </p>
            {!ownership && <p className="text-sm text-gray-400">Loading…</p>}
            {ownership && ownership.sources.length === 0 && <p className="text-sm text-gray-400">No ownership changes.</p>}
            {ownership && ownership.sources.length > 0 && (
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-gray-400 text-left">
                  <th className="py-1 pr-2 font-medium">Source</th>
                  <th className="py-1 pr-2 font-medium text-right">Today</th>
                  <th className="py-1 pr-2 font-medium text-right">Moves</th>
                  <th className="py-1 pr-2 font-medium text-right">From another agent</th>
                  <th className="py-1 pr-2 font-medium text-right">Worked, moved</th>
                  <th className="py-1 font-medium text-right">Overrides</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {ownership.sources.map(r => (
                    <tr key={r.source}>
                      <td className="py-1 pr-2">{r.source}</td>
                      <td className="py-1 pr-2 text-right">{num(r.today)}</td>
                      <td className="py-1 pr-2 text-right">{num(r.moves)}</td>
                      <td className="py-1 pr-2 text-right">{num(r.from_another_agent)}</td>
                      <td className={`py-1 pr-2 text-right ${r.worked_moved > 0 ? 'text-red-600 font-semibold' : ''}`}>{num(r.worked_moved)}</td>
                      <td className="py-1 text-right">{num(r.overrides)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {ownership && ownership.releases.length > 0 && (
              <p className="text-xs text-gray-500 mt-3">
                Left with no owner: {ownership.releases.map(r => `${r.reason} (${num(r.n)})`).join(' · ')}
              </p>
            )}
          </div>
        </div>
      </div>

      {list && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" onClick={() => setList(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">{list.check.label}</p>
                <p className="text-xs text-gray-500">
                  {list.loading ? 'Loading…' : `${num(list.rows.length)} shown${list.rows.length >= 500 ? ' (first 500 — download for the same)' : ''} · live`}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {list.rows.length > 0 && (
                  <button onClick={() => downloadCSV(`health_${list.check.key}`,
                            listCols.map(k => ({ header: k, accessor: (r: Record<string, unknown>) => String(r[k] ?? '') })), list.rows)}
                          className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 dark:border-gray-600 rounded-lg text-sm">
                    <Download className="h-4 w-4" />CSV
                  </button>
                )}
                <button onClick={() => setList(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="overflow-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                  <tr>{listCols.map(k => (
                    <th key={k} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">{k.replace(/_/g, ' ')}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {list.rows.map((r, i) => (
                    <tr key={i}>{listCols.map(k => (
                      <td key={k} className="px-3 py-1.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{String(r[k] ?? '—')}</td>
                    ))}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyHealth;
